import type { Id } from "@repo/convex/dataModel";
import { PROJECT_LEVEL_ROUTE_PATHS } from "@repo/shared";
import { useCallback, useEffect, useState } from "react";
import { type ProjectConversations, useProjectConversations } from "./use-project-conversations";

export type ConversationsFlyout = ProjectConversations & {
  /** Whether the flyout is up. */
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  /**
   * Whether activating the entry opens the flyout, as opposed to going straight to a new chat.
   * False only once the list is known to be empty; the triggers use it to stop advertising a
   * popup they will not open.
   */
  opensFlyout: boolean;
  /** What the Claudia nav entry does when pressed. */
  activate: () => void;
};

/**
 * The Claudia nav entry's flyout, on either rail (LAT-990 D10).
 *
 * The expanded sidebar and the collapsed icon strip open the *same* switcher off the same
 * subscription, so it is one hook rather than two copies that can drift — and it owns the
 * things that must always travel together:
 *
 * - **The subscription is skipped while the tab is not the one on screen.** Every open project
 *   keeps its sidebar mounted (`ProjectSidebarsHost`), so without this each background tab held
 *   a live conversation list open for a rail nobody can see.
 * - **Leaving the tab closes the flyout.** It is portaled to the body, so it outlives the
 *   `display: none` its keep-alive host puts over the sidebar behind it.
 * - **A project with no conversation skips the flyout** (LAT-1077). The flyout exists to pick a
 *   conversation; with none to pick, its only live control is "New chat", so the entry goes
 *   there directly. The shortcut waits for the query to answer: navigating on "no rows *yet*"
 *   would send someone with a dozen conversations into a fresh chat for a frame. An archived
 *   conversation is still a prior chat: whoever put their last one away keeps the flyout, whose
 *   "View all conversations" is the way back to "Show archived".
 */
export function useConversationsFlyout({
  projectId,
  isActive = true,
  onNavigate,
}: {
  projectId: Id<"projects"> | null | undefined;
  /** Defaults to true for a rail that only ever renders while it is the visible one. */
  isActive?: boolean;
  /** The rail's own tab navigation; the flyout's rows and the new-chat shortcut both use it. */
  onNavigate: (path: string) => void;
}): ConversationsFlyout {
  const subscribedProjectId = isActive ? projectId : null;
  const conversations = useProjectConversations({ projectId: subscribedProjectId });
  const [isOpen, setIsOpen] = useState(false);

  const hasNoActiveConversation =
    Boolean(subscribedProjectId) && !conversations.isLoading && conversations.items.length === 0;
  // The archived question is asked only once the active list has answered "none" — a handful of
  // rows at most, and never on a rail with live conversations. Folding `includeArchived` into the
  // subscription above instead would make archived rows share the list's history cap with the
  // rows the flyout actually draws.
  const history = useProjectConversations({
    projectId: hasNoActiveConversation ? subscribedProjectId : null,
    includeArchived: true,
  });
  const isKnownEmpty = hasNoActiveConversation && !history.isLoading && history.items.length === 0;
  const opensFlyout = !isKnownEmpty;

  const activate = useCallback(() => {
    if (isOpen) {
      setIsOpen(false);
      return;
    }
    if (opensFlyout) {
      setIsOpen(true);
      return;
    }
    onNavigate(PROJECT_LEVEL_ROUTE_PATHS.aiAssistantNewChat);
  }, [isOpen, onNavigate, opensFlyout]);

  useEffect(() => {
    if (!isActive) {
      setIsOpen(false);
    }
  }, [isActive]);

  return { ...conversations, isOpen, setIsOpen, opensFlyout, activate };
}
